import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoneyIcon from "@mui/icons-material/Money";
import PeopleIcon from "@mui/icons-material/PeopleOutlined";
import ThreePIcon from "@mui/icons-material/ThreeP";
import {
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import { format } from "date-fns";
import { Formik } from "formik";
import MaterialReactTable from "material-react-table";
import { ContainedButton } from "src/components/button-group";
import FormikControl from "src/components/formik/FormikControl";
import { InfoCard } from "src/components/info-cards/info-card";
import { OrderStatusChip } from "src/components/pages/order/order-status-chip";
import { RoutePaths } from "src/constants";
import {
  selectDayWiseOrders,
  selectTotalComplaints,
  selectTotalCustomers,
  selectTotalOrders,
  selectTotalOrdersByServices,
  selectTotalOrdersByStatus,
} from "src/slices/dashboard/selectors";
import {
  fetchDayWiseOrders,
  fetchTotalComplaints,
  fetchTotalCustomers,
  fetchTotalOrders,
  fetchTotalOrdersByServices,
  fetchTotalOrdersByStatus,
} from "src/slices/dashboard/thunks";
import { selectFilteredOrders } from "src/slices/orders/selectors";
import { fetchFilteredOrders } from "src/slices/orders/thunks";
import { IFetchFilteredOrdersPayload } from "src/slices/orders/types";
import { AppDispatch } from "src/store";
import { dayMonthYearFormat } from "src/lib/utils";
import { KeyValueListCard } from "src/components/info-cards/key-value-list-card";
import { BarChartCard } from "src/components/info-cards/bar-chart-card";
import { selectLocations } from "src/slices/configuration/selectors";
import { fetchLocations } from "src/slices/configuration/thunks";

export function OperationalDashboardPanel() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const formikRef = useRef(null);
  const filteredOrder = useSelector(selectFilteredOrders);
  const totalOrders = useSelector(selectTotalOrders);
  const totalComplaints = useSelector(selectTotalComplaints);
  const totalCustomers = useSelector(selectTotalCustomers);
  const totalOrdersByStatus = useSelector(selectTotalOrdersByStatus);
  const totalOrdersByServices = useSelector(selectTotalOrdersByServices);
  const locations = useSelector(selectLocations);

  const [orderColumns] = useState<any>([
    { accessorKey: "orderId", header: "Order#" },
    {
      accessorKey: "customerName",
      header: "Customer Name",
    },
    {
      accessorKey: "pickUpDate",
      header: "Pickup Date",
    },
    {
      accessorKey: "dueDate",
      header: "Due Date",
    },
    {
      accessorKey: "orderStatus",
      header: "Order Status",
      Cell: ({ cell }) => <OrderStatusChip orderStatus={cell.getValue()} />,
    },
    {
      accessorKey: "pickup",
      header: "Pickup",
      Cell: ({ cell }) => (
        <>
          {!["", null, undefined].includes(cell.getValue()) ? (
            <Tooltip title="Copy">
              <IconButton
                onClick={() =>
                  navigator.clipboard.writeText(cell.getValue().toString())
                }
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      ),
    },
    {
      accessorKey: "delivery",
      header: "Delivery",
      Cell: ({ cell }) => (
        <>
          {!["", null, undefined].includes(cell.getValue()) ? (
            <Tooltip title="Copy">
              <IconButton
                onClick={() =>
                  navigator.clipboard.writeText(cell.getValue().toString())
                }
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      ),
    },
  ]);

  const [orderRows, setOrderRows] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState(-1);
  const [duration, setDuration] = useState({
    order: "monthly",
    customer: "monthly",
    complaint: "monthly",
  });

  useEffect(() => {
    dispatch(fetchLocations());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!formikRef.current) return;
    const { startDate, endDate } = formikRef.current.values;
    const payload: IFetchFilteredOrdersPayload = {
      startDate: format(new Date(startDate), "yyyy-MM-dd"),
      endDate: format(new Date(endDate), "yyyy-MM-dd"),
    };
    const queryParams: { branchId?: number } = {};
    if (selectedBranch !== -1) {
      queryParams.branchId = selectedBranch;
    }

    dispatch(fetchFilteredOrders(payload));
    dispatch(
      fetchTotalCustomers({ ...queryParams, filter: duration.customer })
    );
    dispatch(fetchTotalOrders({ ...queryParams, filter: duration.order }));
    dispatch(
      fetchTotalComplaints({ ...queryParams, filter: duration.complaint })
    );
    dispatch(fetchTotalOrdersByStatus(queryParams));
    dispatch(fetchTotalOrdersByServices(queryParams));

    // eslint-disable-next-line
  }, [selectedBranch, duration]);

  useEffect(() => {
    if (filteredOrder.length > 0) {
      const tableOrderRows: any[] = filteredOrder.map((order) => ({
        id: order.id,
        customerId: order.customerId,
        orderId: `LX-${order.id}`,
        customerName: order.customerName,
        pickupAddress: order.pickupAddress,
        deliveryAddress: order.deliveryAddress,
        orderStatus: order.orderStatus,
        servicesItems: order.servicesItems,
        pickup: order.pickupAddressGoogleLocation,
        delivery: order.deliveryAddressGoogleLocation,
        pickUpDate: dayMonthYearFormat(new Date(order.pickUpDate)),
        dueDate: dayMonthYearFormat(new Date(order.dueDate)),
      }));
      setOrderRows(tableOrderRows);
    }
  }, [filteredOrder]);

  const dayWiseOrders = useSelector(selectDayWiseOrders);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStatus, setSelectedStatus] = useState(-1);

  useEffect(() => {
    const queryParams: any = {
      month: selectedMonth,
      year: selectedYear,
    };
    if (selectedBranch !== -1) queryParams.branchId = selectedBranch;
    if (selectedStatus !== -1) queryParams.byStatus = selectedStatus;
    dispatch(fetchDayWiseOrders(queryParams));
  }, [selectedMonth, selectedYear, selectedBranch, selectedStatus, dispatch]);

  // useEffect(() => {
  //   console.log("selectedStatus", selectedStatus);
  // }, [selectedStatus]);
  return (
    <Box sx={{ width: 1 }}>
      <Grid container spacing={2}>
        <Grid container item xs={12} justifyContent={"end"}>
          <Grid item xs={4}>
            <InputLabel id="demo-simple-select-label">Branch</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Branch"
              fullWidth
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value as number)}
            >
              <MenuItem value={-1}>{"All"}</MenuItem>
              {locations.map((loc) => (
                <MenuItem value={loc.id}>{loc.name}</MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <InfoCard
            title="TOTAL ORDERS"
            value={totalOrders?.totalOrders}
            icon={<MoneyIcon />}
            iconBackgoroundColor="primary.main"
            setDuration={(value) =>
              setDuration((prev) => ({ ...prev, order: value }))
            }
            duration={duration.order}
            barChartList={totalOrders?.data?.map((data) => ({
              ...data,
              value: parseInt(data.totalorders),
            }))}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoCard
            title="DAY-WISE ORDERS"
            value={dayWiseOrders?.data?.totalOrders || 0}
            icon={<MoneyIcon />}
            iconBackgoroundColor="primary.main"
            duration="daily" // ✅ Force daily view
            barChartList={Array.from({ length: 31 }, (_, i) => {
              const day = i + 1;
              const order = dayWiseOrders?.data?.orders?.find(
                (d) => d.day === day
              );
              return {
                day,
                value: order ? parseInt(order.totalorders) : 0,
              };
            })}
            customFilter={
              <Grid container spacing={1}>
                {/* ✅ Status Filter */}
                <Grid item xs={5}>
                  <Select
                    size="small"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(Number(e.target.value))}
                    fullWidth
                    sx={{ minWidth: 100 }}
                  >
                    <MenuItem value={-1}>All Statuses</MenuItem>
                    {[
                      "NEW ORDER",
                      "READY FOR PICKUP",
                      "IN PROGRESS",
                      "READY FOR DELIVERY",
                      "DELIVERED",
                      "PICKUP FAILED",
                      "DELIVERY FAILED",
                      "CANCELLED",
                      // "REORDER",
                      // "RESCHEDULE",
                    ].map((status, i) => (
                      <MenuItem key={status} value={i + 1}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* ✅ Month Filter */}
                <Grid item xs={4}>
                  <Select
                    size="small"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    fullWidth
                    sx={{ minWidth: 80 }}
                  >
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((m, i) => (
                      <MenuItem key={i} value={i + 1}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* ✅ Year Filter */}
                <Grid item xs={3}>
                  <Select
                    size="small"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    fullWidth
                    sx={{ minWidth: 60 }}
                  >
                    {[2023, 2024, 2025].map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <InfoCard
            title="TOTAL CUSTOMERS"
            value={totalCustomers?.totalCustomers}
            icon={<PeopleIcon />}
            duration={duration.customer}
            setDuration={(value) =>
              setDuration((prev) => ({ ...prev, customer: value }))
            }
            iconBackgoroundColor="primary.main"
            barChartList={
              totalCustomers?.data?.rows?.map((data) => ({
                ...data,
                value: parseInt(data.totalcustomers),
              })) || []
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InfoCard
            title="TOTAL COMPLAINTS"
            value={totalComplaints?.totalComplaints}
            icon={<ThreePIcon />}
            duration={duration.complaint}
            setDuration={(value) =>
              setDuration((prev) => ({ ...prev, complaint: value }))
            }
            iconBackgoroundColor="primary.main"
            barChartList={
              totalComplaints?.data?.map((data) => ({
                ...data,
                value: parseInt(data.totalcomplaints),
              })) || []
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KeyValueListCard
            title="ORDERS BY STATUS"
            keyValueList={totalOrdersByStatus.map((data) => ({
              key: data.name,
              value: data.totalstatus,
              onClick: () => {
                // console.log("datadata", selectedBranch);

                navigate(RoutePaths.ORDER_LISTING, {
                  state: {
                    defaultOrderStatus: data.name,
                    defaultOrderBranch: selectedBranch,
                  },
                });
              },
            }))}
          />
        </Grid>
        <Grid item xs={12}>
          <BarChartCard
            title="ORDERS BY SERVICES"
            barChartList={totalOrdersByServices.map((data) => ({
              xTitle: data.name,
              value: parseInt(data.totalservices),
            }))}
          />
        </Grid>

        <Grid item xs={12}>
          <MaterialReactTable
            columns={orderColumns}
            data={orderRows}
            initialState={{
              density: "compact",
              pagination: { pageIndex: 0, pageSize: 10 },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  p: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                <Formik
                  initialValues={{
                    startDate: new Date(
                      new Date().setDate(new Date().getDate() - 7)
                    ),
                    endDate: new Date(),
                  }}
                  innerRef={formikRef}
                  onSubmit={() => {}}
                >
                  {({ values, touched, errors, getFieldProps }) => (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikControl
                          control="CalendarTime"
                          label="Start Date"
                          dateOrTimeOnly="date"
                          name="startDate"
                          error={Boolean(touched.startDate && errors.startDate)}
                          helperText={touched.startDate && errors.startDate}
                          {...getFieldProps("startDate")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikControl
                          control="CalendarTime"
                          label="End Date"
                          dateOrTimeOnly="date"
                          name="endDate"
                          error={Boolean(touched.endDate && errors.endDate)}
                          helperText={touched.endDate && errors.endDate}
                          {...getFieldProps("endDate")}
                        />
                      </Grid>
                      <Grid item>
                        <ContainedButton
                          onClick={() => {
                            const payload: IFetchFilteredOrdersPayload = {
                              startDate: format(values.startDate, "yyyy-MM-dd"),
                              endDate: format(values.endDate, "yyyy-MM-dd"),
                              branchId:
                                selectedBranch !== -1
                                  ? selectedBranch
                                  : undefined,
                            };
                            dispatch(fetchFilteredOrders(payload));
                          }}
                        >
                          Search
                        </ContainedButton>
                      </Grid>
                    </Grid>
                  )}
                </Formik>
              </Box>
            )}
            enableRowActions
            renderRowActionMenuItems={({ closeMenu, row }) => [
              <MenuItem
                key={1}
                onClick={() => {
                  closeMenu();
                  navigate(`${RoutePaths.ORDER_DETAILS}/${row.original.id}`);
                }}
                sx={{ m: 0 }}
              >
                Order Details
              </MenuItem>,
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
